import type { ProcessDetail as ProcessDetailDto } from '@bk/contracts';
import type { Procedure, ProcessContract } from '@bk/process-dsl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';
import { ContractEditor } from './ContractEditor';
import { ContractView } from './ContractView';
import { ObservationPanel } from './Observation';
import { RunRow } from './Runs';
import { ScopeControl } from './SiteScope';
import { StartRun } from './StartRun';

export function ProcessDetailPage({ processId }: { processId: string }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const detail = useTenantQuery<ProcessDetailDto>(['processes', processId], `/processes/${processId}`);
  const [mode, setMode] = useState<'view' | 'start' | 'edit'>('view');
  const [source, setSource] = useState('');
  const [valid, setValid] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const ratify = useMutation({ mutationFn: (procedureId: string) => api<ProcessDetailDto>(t(`/processes/${processId}/procedures/${procedureId}/ratify`), { method: 'POST' }), onSuccess: refresh });
  const restore = useMutation({ mutationFn: (procedureId: string) => api<void>(t(`/processes/${processId}/procedures/${procedureId}/restore`), { method: 'POST' }), onSuccess: refresh });
  const publish = useMutation({
    mutationFn: () => api<ProcessDetailDto>(t(`/processes/${processId}/contracts`), { body: { source } }),
    onSuccess: () => {
      setMode('view');
      refresh();
    },
  });

  if (!detail.data) return <ErrorText>{detail.error ? errorMessage(detail.error) : ''}</ErrorText>;
  const p = detail.data;
  const contract = p.contract.parsed as ProcessContract;
  const active = p.procedures.find((v) => v.status === 'attiva');
  const pending = p.procedures.find((v) => v.status === 'proposta');
  // Titolari e amministratori, o responsabili delle procedure dell'area del processo.
  const canEdit = p.can_edit ?? isManager;

  return (
    <>
      <PageTitle
        title={p.name}
        subtitle={`Contratto v${p.contract_version} · ${active ? `procedura v${active.version} attiva` : 'nessuna procedura attiva'}`}
        actions={
          (canEdit || isManager) &&
          mode === 'view' && (
            <div className="flex gap-2">
              {canEdit && (
                <>
                  <Button variant="ghost" onClick={() => navigate(`/t/${tenant.id}/processi/rivedi/${processId}`)}>
                    Rivedi con il sistema
                  </Button>
                  <Button
                    variant="ghost"
                    title="Formato interno, per uso tecnico"
                    onClick={() => {
                      setSource(p.contract.source);
                      setMode('edit');
                    }}
                  >
                    Editor avanzato
                  </Button>
                </>
              )}
              {isManager && (
                <Button disabled={!active} onClick={() => setMode('start')} title={active ? '' : 'Ratificare prima la procedura'}>
                  Avvia
                </Button>
              )}
            </div>
          )
        }
      />
      <div className="-mt-3 mb-4">
        <ScopeControl tipo="processo" id={processId} ambito={p.ambito} />
      </div>
      <ErrorText>{ratify.error ? errorMessage(ratify.error) : restore.error ? errorMessage(restore.error) : ''}</ErrorText>
      {mode === 'view' && <ObservationPanel processId={processId} canEdit={canEdit} />}

      {pending && mode === 'view' && (
        <Card className="mb-6 border-amber-300 bg-amber-50/60">
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium">Procedura v{pending.version} proposta</p>
              <p className="text-sm text-ink/65">
                Derivata dal contratto v{p.contract_version}.{' '}
                {active ? `Finché non la ratifichi, le esecuzioni seguono la procedura v${active.version}.` : 'Finché non la ratifichi, il processo non si può avviare.'}
              </p>
            </div>
            {canEdit && (
              <Button disabled={ratify.isPending} onClick={() => ratify.mutate(pending.id)}>
                Ratifica
              </Button>
            )}
          </div>
          <div className="mt-4">
            <ContractView contract={contract} procedure={pending.steps as Procedure} />
          </div>
        </Card>
      )}

      {mode === 'start' && <StartRun processId={processId} contract={contract} scope={p.ambito} onCancel={() => setMode('view')} />}

      {mode === 'edit' && (
        <Card>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <p className="flex-1 text-sm text-ink/65">La nuova versione genera una procedura da ratificare; fino ad allora le esecuzioni seguono quella attiva.</p>
            <Button disabled={!valid || publish.isPending} onClick={() => publish.mutate()}>
              Pubblica versione {p.contract_version + 1}
            </Button>
            <Button variant="ghost" onClick={() => setMode('view')}>
              Annulla
            </Button>
          </div>
          <ErrorText>{publish.error ? errorMessage(publish.error) : ''}</ErrorText>
          <ContractEditor source={source} onChange={setSource} onValid={setValid} />
        </Card>
      )}

      {mode === 'view' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
          <Card>
            <ContractView contract={contract} procedure={(active?.steps ?? pending?.steps) as Procedure | undefined} />
            <details className="mt-6 text-sm">
              <summary className="cursor-pointer text-ink/55">Avanzato: contratto nel formato interno (YAML)</summary>
              <pre className="mt-2 overflow-auto rounded-lg bg-black/[0.03] p-3 text-xs">{p.contract.source}</pre>
            </details>
          </Card>
          <div className="space-y-6">
            <Card>
              <h2 className="mb-2 text-sm font-semibold">Esecuzioni recenti</h2>
              {p.runs.length ? (
                <div className="divide-y divide-black/5">
                  {p.runs.map((r) => (
                    <RunRow key={r.id} run={r} />
                  ))}
                </div>
              ) : (
                <Empty>Nessuna esecuzione.</Empty>
              )}
            </Card>
            <Card>
              <h2 className="mb-2 text-sm font-semibold">Versioni</h2>
              <ul className="space-y-1 text-sm">
                {p.procedures.map((v) => (
                  <li key={v.id} className="flex items-center gap-2">
                    <span className="flex-1">Procedura v{v.version}</span>
                    <Badge tone={v.status === 'attiva' ? 'green' : v.status === 'proposta' ? 'amber' : v.status === 'osservazione' ? 'blue' : 'gray'}>{v.status}</Badge>
                    <span className="text-xs text-ink/45">{dateTime(v.ratified_at ?? v.created_at)}</span>
                    {canEdit && v.status === 'ritirata' && v.ratified_at && (
                      <button className="text-xs text-accent hover:underline" disabled={restore.isPending} onClick={() => confirm(`Ripristinare la procedura v${v.version} come attiva?`) && restore.mutate(v.id)}>
                        Ripristina
                      </button>
                    )}
                  </li>
                ))}
                {p.contracts.map((c) => (
                  <li key={c.id} className="flex items-center gap-2">
                    <span className="flex-1">Contratto v{c.version}</span>
                    <Badge tone={c.status === 'attiva' ? 'green' : 'gray'}>{c.status}</Badge>
                    <span className="text-xs text-ink/45">{dateTime(c.created_at)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
